/**
 * Custom Element: ElkProductCarousel
 * Implements a 3-image carousel for product cards.
 * Desktop: Autoplay on hover (cycles every 1.5s).
 * Mobile: Native snap-scrolling with dash synchronization.
 */
class ElkProductCarousel extends HTMLElement {
  constructor() {
    super();
    this.wrapper = this.querySelector('.elk-product-carousel__wrapper');
    this.items = this.querySelectorAll('.elk-product-carousel__item');
    this.dashes = this.querySelectorAll('.elk-product-carousel__dash');
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.isMobile = window.matchMedia("(max-width: 999px)").matches;
    this.interval = 1500; // 1.5 seconds
  }

  connectedCallback() {
    if (!this.wrapper || this.items.length === 0) return;

    // Desktop hover logic: Autoplay
    if (!this.isMobile) {
      this.parentElement.addEventListener('mouseenter', () => this.startAutoplay());
      this.parentElement.addEventListener('mouseleave', () => this.stopAutoplay());

      // Allow manual clicking on dashes too
      this.dashes.forEach((dash, index) => {
        dash.addEventListener('click', (e) => {
          e.preventDefault();
          this.stopAutoplay();
          this.goToIndex(index);
        });
      });
    } else {
      // Mobile scroll sync
      this.wrapper.addEventListener('scroll', () => this.handleScroll(), { passive: true });
    }
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      let nextIndex = (this.currentIndex + 1) % this.items.length;
      this.goToIndex(nextIndex);
    }, this.interval);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    // Optional: Reset to first image on leave
    this.goToIndex(0);
  }

  goToIndex(index) {
    if (index === this.currentIndex && !this.isMobile) {
      // Reset check if it's already 0 we still want to ensure visibility
    }

    this.currentIndex = index;

    // Update dashes
    this.dashes.forEach((dash, i) => {
      dash.classList.toggle('is-active', i === index);
    });

    // Update items (for desktop hover effect)
    if (!this.isMobile) {
      this.items.forEach((item, i) => {
        item.classList.toggle('is-active', i === index);
      });
    } else {
      // For mobile, scroll the wrapper
      const itemWidth = this.items[0].offsetWidth;
      this.wrapper.scrollTo({
        left: itemWidth * index,
        behavior: 'smooth'
      });
    }
  }

  handleScroll() {
    const scrollLeft = this.wrapper.scrollLeft;
    const itemWidth = this.items[0].offsetWidth;
    const index = Math.round(scrollLeft / itemWidth);

    if (index !== this.currentIndex && index >= 0 && index < this.items.length) {
      this.currentIndex = index;
      this.dashes.forEach((dash, i) => {
        dash.classList.toggle('is-active', i === index);
      });
    }
  }
}

if (!customElements.get('elk-product-carousel')) {
  customElements.define('elk-product-carousel', ElkProductCarousel);
}

/**
 * Keep second-level sidebar menus open across drawer close/reopen.
 * Prestige's HeaderSidebar collapses every <details> on "dialog:after-hide",
 * which defeats the "Open second-level menus by default" setting after the
 * first close. Re-open them once the theme's own handler has run.
 */
(function () {
  const sidebar = document.getElementById('sidebar-menu');
  if (!sidebar || !sidebar.hasAttribute('data-open-second-level')) return;
  sidebar.addEventListener('dialog:after-hide', () => {
    setTimeout(() => {
      sidebar.querySelectorAll('details.accordion__disclosure').forEach((d) => { d.open = true; });
    }, 0);
  });
})();
